import { Quill } from "react-quill-new";
import Inline from "quill/blots/inline";
import {
  QUILL_TEXT_NODE,
  preserveLineStartSpacesMatcher,
} from "@/utils/quillClipboard";

const MAX_QUILL_INDENT = 8;

// Instructor emphasis (지문 음영) inline format.
// CSS class only — no inline style, so future color changes are
// data-migration free. Toolbar button is mounted only on the passage editor,
// but registration is global so paste/load works across all Quill instances.
export const INSTRUCTOR_EMPHASIS_CLASS = "instructor-emphasis";

class InstructorEmphasisBlot extends Inline {
  static blotName = "instructorEmphasis";
  static tagName = "span";
  static className = INSTRUCTOR_EMPHASIS_CLASS;

  // Required for class-based span Inline blot. Default Inline.formats returns
  // undefined when tagName matches the base SPAN inline blot, which causes
  // optimize() to unwrap the span immediately after creation.
  static formats(domNode: HTMLElement) {
    return domNode.classList.contains(INSTRUCTOR_EMPHASIS_CLASS);
  }
}

try {
  Quill.register(InstructorEmphasisBlot, true);
} catch (error) {
  console.warn("Instructor emphasis blot registration failed:", error);
}

type QuillSelection = {
  index: number;
  length: number;
};

type QuillEditor = {
  getSelection: () => QuillSelection | null;
  getLine: (index: number) => [unknown, number];
  getFormat: (index: number, length?: number) => Record<string, unknown>;
  formatLine: (
    index: number,
    length: number,
    name: string,
    value: unknown
  ) => void;
  focus: () => void;
};

type FormulaQuillEditor = QuillEditor & {
  getText: (index: number, length: number) => string;
  deleteText: (index: number, length: number) => void;
  insertEmbed: (index: number, type: string, value: string) => void;
  setSelection: (index: number) => void;
};

type ClipboardDeltaOp = {
  attributes?: Record<string, unknown>;
  [key: string]: unknown;
};

type ClipboardDelta = {
  ops?: ClipboardDeltaOp[];
};

type TabDirection = -1 | 1;

type NoToolbarQuillModulesOptions = {
  onTab?: (direction: TabDirection) => boolean;
};

export const applyQuillIndent = (
  quill: QuillEditor,
  increase: boolean
): boolean => {
  const selection = quill.getSelection();
  if (!selection) return false;

  const [, offset] = quill.getLine(selection.index);
  const lineStart = selection.index - offset;
  const currentFormat = quill.getFormat(lineStart, 1);
  const currentIndent = Number(currentFormat.indent || 0);
  const nextIndent = increase
    ? Math.min(currentIndent + 1, MAX_QUILL_INDENT)
    : Math.max(currentIndent - 1, 0);

  if (nextIndent === currentIndent) {
    return false;
  }

  quill.formatLine(lineStart, 1, "indent", nextIndent);
  return true;
};

const createIndentShortcutHandler =
  (increase: boolean) =>
  function (this: { quill?: QuillEditor }) {
    if (!this.quill) return true;
    applyQuillIndent(this.quill, increase);
    return false;
  };

const DISABLED_TAB_BINDINGS = {
  indent: null,
  outdent: null,
  "indent code-block": null,
  "outdent code-block": null,
  "remove tab": null,
  tab: null,
  "table tab": null,
};

const createTabNavigationBindings = (
  onTab?: NoToolbarQuillModulesOptions["onTab"]
) =>
  onTab
    ? {
        "tab-navigation": {
          key: "Tab",
          shiftKey: null,
          handler: function (
            _range: QuillSelection,
            context: { event: KeyboardEvent }
          ) {
            const moved = onTab(context.event.shiftKey ? -1 : 1);
            return !moved;
          },
        },
      }
    : {};

export const createNoToolbarQuillModules = (
  options: NoToolbarQuillModulesOptions = {}
) => ({
  toolbar: false,
  keyboard: {
    bindings: {
      ...DISABLED_TAB_BINDINGS,
      ...createTabNavigationBindings(options.onTab),
      "formula-complete": {
        key: "Enter",
        handler: function (
          this: { quill: FormulaQuillEditor },
          range: QuillSelection
        ) {
          const quill = this.quill;
          const text = quill.getText(0, range.index);
          const match = text.match(/(\$\$)([\s\S]+)(\$\$)$/);

          if (!match) {
            return true;
          }

          const formulaText = match[2].trim();
          const matchedTextLength = match[0].length;
          const startIndex = range.index - matchedTextLength;

          quill.deleteText(startIndex, matchedTextLength);
          quill.insertEmbed(startIndex, "formula", formulaText);
          quill.setSelection(startIndex + 1);

          return false;
        },
      },
      "increase-indent": {
        key: "]",
        ctrlKey: true,
        handler: createIndentShortcutHandler(true),
      },
      "decrease-indent": {
        key: "[",
        ctrlKey: true,
        handler: createIndentShortcutHandler(false),
      },
    },
  },
  clipboard: {
    matchVisual: false,
    matchers: [
      [QUILL_TEXT_NODE, preserveLineStartSpacesMatcher],
      [
        "IMG",
        (_node: unknown, delta: unknown) => {
          return delta;
        },
      ],
      [
        "*",
        (node: HTMLElement, delta: ClipboardDelta) => {
          if (node.style) {
            node.removeAttribute("style");
          }

          const ops = delta.ops || [];
          return {
            ops: ops.map((op) => {
              if (op.attributes) {
                const restAttributes = { ...op.attributes };
                delete restAttributes.background;
                delete restAttributes.color;
                return {
                  ...op,
                  attributes:
                    Object.keys(restAttributes).length > 0
                      ? restAttributes
                      : undefined,
                };
              }
              return op;
            }),
          };
        },
      ],
    ],
  },
} as const);

export const NO_TOOLBAR_QUILL_MODULES = createNoToolbarQuillModules();
